import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import excelIcon from '../../../assets/img/icons/excel-green.svg';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import BottomPagination from '../../../components/Pagination/BottomPagination/BottomPagination';
import TopPagination from '../../../components/Pagination/TopPagination/TopPagination';
import {
    ButtonIcon,
    WhiteButtonWithIcon,
} from '../../../styledComponents/buttons';
import {
    CommonFlex,
    CommonGrid,
    HeaderContainer,
    PageTitle,
} from '../../../styledComponents/common';
import { exportToExcel } from '../../../utils/exportToExcel';
import CateringTaskFilter from './CateringTaskFilter';
import CateringTaskReportList from './CateringTaskReportList';

const CateringTaskReport = ({
    selectedOrganization,
    getOrganizationMembersList,
    orgMembersList,
    isLoading,
    locationsList,
    getTaskLocation,
    getCateringTasksList,
    cateringTasksList,
    totalTasksCount,
    pageSize,
}) => {
    const { t } = useTranslation();
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState({
        startDate: moment(),
        endDate: moment(),
    });
    const [selectedLocation, setSelectedLocation] = useState({
        label: t('all_locations'),
        value: null,
    });
    const [selectedMember, setSelectedMember] = useState('');
    const [taskStatus, setTaskStatus] = useState({
        label: t('all_status'),
        value: null,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [search, setSearch] = useState(false);
    const [currentSearchedTerm, setCurrentSearchedTerm] = useState('');
    const [expandedTaskId, setExpandedTaskId] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);

    const handleSearchByName = () => {
        setCurrentSearchedTerm(searchTerm);
        setSearch(!search);
        setCurrentPageNumber(1);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentSearchedTerm('');
        setSearch(!search);
    };

    const handlePageChange = page => {
        setCurrentPageNumber(page);
    };

    const selectOffice = e => {
        setCurrentPageNumber(1);
        setSearchTerm('');
        setCurrentSearchedTerm('');
        setSelectedLocation(e);
    };

    const selectMember = person => {
        setSelectedMember(person);
        setSearchTerm('');
        setCurrentSearchedTerm('');
        setCurrentPageNumber(1);
    };

    const clearMember = () => {
        setSelectedMember('');
        setSearchTerm('');
        setCurrentSearchedTerm('');
        setCurrentPageNumber(1);
    };

    const onSearchTermChange = e => {
        const { value } = e.target;
        if (value !== '') {
            setSelectedMember('');
        }
        setSearchTerm(value);
    };

    const handleStatusChange = e => {
        setTaskStatus(e);
        setCurrentPageNumber(1);
    };

    const handleDateChange = date => {
        setSelectedDate(date);
        setCurrentPageNumber(1);
    };

    const getTasks = () => {
        const payload = {
            startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
            endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
            page: currentPageNumber,
        };
        if (selectedLocation && selectedLocation.value) {
            payload.location_id = selectedLocation.value;
        }
        if (selectedMember && selectedMember.id) {
            payload.assignee_id = selectedMember.id;
        }
        if (searchTerm && searchTerm.trim()) {
            payload.task_name = searchTerm.toLowerCase().trim();
        }
        if (taskStatus && taskStatus.value && taskStatus.value !== 'all') {
            payload.status = taskStatus.value;
        }

        getCateringTasksList(payload);
    };

    useEffect(() => {
        getTasks();
    }, [
        currentPageNumber,
        selectedDate,
        selectedLocation,
        selectedMember,
        search,
        taskStatus,
    ]);

    useEffect(() => {
        setSelectedLocation({
            label: t('all_locations'),
            value: null,
        });
    }, [t]);

    useEffect(() => {
        setTaskStatus({
            label: t('all_status'),
            value: null,
        });
    }, [t]);

    const handleExportToExcel = async () => {
        const startDate = moment(selectedDate.startDate).format('YYYY-MM-DD');
        const endDate = moment(selectedDate.endDate).format('YYYY-MM-DD');

        let url = `tasks/export-catering-tasks/?required_start_date=${startDate}&required_end_date=${endDate}`;
        if (selectedLocation?.value) url += `&location_id=${selectedLocation.value}`;
        if (selectedMember?.id) url += `&assignees=${selectedMember.id}`;
        if (searchTerm?.trim()) url += `&name=${searchTerm.toLowerCase().trim()}`;
        if (taskStatus?.value && taskStatus.value !== 'all') url += `&status=${taskStatus.value}`;

        const fileName = `Catering_Report_${startDate}_to_${endDate}.xlsx`;
        setExportLoading(true);
        try {
            await exportToExcel(url, fileName, true);
        } catch (error) {
            // error notification is already shown inside exportToExcel
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className="content">
            <CommonGrid alignItem="center">
                <PageTitle>{t('catering_task_report')}</PageTitle>
                <CommonFlex gap="12px">
                    <WhiteButtonWithIcon
                        onClick={handleExportToExcel}
                        color="#19AC65"
                        borderColor="#19AC65"
                        hoverBorderColor="#129652"
                        disabled={exportLoading}
                    >
                        {exportLoading ? (
                            <ButtonTextLoader color="#19AC65" loadingText=" " size={6} />
                        ) : (
                            <>
                                <ButtonIcon src={excelIcon} size="26px" />
                                {t('export_report')}
                            </>
                        )}
                    </WhiteButtonWithIcon>
                </CommonFlex>
            </CommonGrid>
            <HeaderContainer columns="1fr">
                <CateringTaskFilter
                    getOrganizationMembersList={getOrganizationMembersList}
                    organizationMembersList={orgMembersList}
                    locationsList={locationsList}
                    getTaskLocation={getTaskLocation}
                    selectedLocation={selectedLocation}
                    selectOffice={selectOffice}
                    selectedMember={selectedMember}
                    selectMember={selectMember}
                    clearMember={clearMember}
                    taskStatus={taskStatus}
                    selectedDate={selectedDate}
                    startDate={selectedDate.startDate}
                    endDate={selectedDate.endDate}
                    searchTerm={searchTerm}
                    onSearchTermChange={onSearchTermChange}
                    handleDateChange={handleDateChange}
                    handleStatusChange={handleStatusChange}
                    handleSearchByName={handleSearchByName}
                    handleClearSearch={handleClearSearch}
                />
            </HeaderContainer>

            {totalTasksCount && totalTasksCount > 0 && pageSize ? (
                <TopPagination
                    itemName={t('catering_task_report')}
                    totalCount={totalTasksCount}
                    currentPage={currentPageNumber}
                    onPageChange={page => handlePageChange(page)}
                    pageSize={pageSize}
                />
            ) : null}

            <CateringTaskReportList
                cateringTasksList={cateringTasksList}
                isLoading={isLoading}
                expandedTaskId={expandedTaskId}
                setExpandedTaskId={setExpandedTaskId}
            />

            {totalTasksCount && totalTasksCount > 0 && pageSize ? (
                <BottomPagination
                    totalCount={totalTasksCount}
                    currentPage={currentPageNumber}
                    onPageChange={page => handlePageChange(page)}
                    pageSize={pageSize}
                />
            ) : null}
        </div>
    );
};

export default CateringTaskReport;
